'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import type { CriticalEntity } from '@/lib/command-center/types';

interface ReliabilityWidgetProps {
  workspaceId: string;
}

interface ReliabilityStats {
  total_entities: number;
  entities_with_incidents: number;
  avg_availability: number | null;
  spof_count: number;
  recent_incidents: number;
  critical_entities: CriticalEntity[];
}

export function ReliabilityWidget({ workspaceId }: ReliabilityWidgetProps) {
  const [stats, setStats] = useState<ReliabilityStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await fetch(
          `/api/secure/command-center/dependencies/critical-paths?workspace_id=${workspaceId}`
        );

        if (!response.ok) {
          throw new Error('Failed to fetch reliability stats');
        }

        const data = await response.json();

        setStats({
          total_entities: data.summary?.total_entities || 0,
          entities_with_incidents: 0, // Would need additional query
          avg_availability: null, // Would need additional query
          spof_count: data.summary?.single_points_of_failure || 0,
          recent_incidents: 0, // Would need additional query
          critical_entities: data.critical_entities || [],
        });
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load stats');
      } finally {
        setLoading(false);
      }
    };

    if (workspaceId) {
      fetchStats();
    }
  }, [workspaceId]);

  if (loading) {
    return (
      <div className="bg-surface/50 rounded-xl border border-border-muted p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-medium text-foreground">Reliability Overview</h3>
        </div>
        <div className="flex items-center justify-center py-8">
          <div className="w-6 h-6 border-2 border-border border-t-orange rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-surface/50 rounded-xl border border-border-muted p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-medium text-foreground">Reliability Overview</h3>
        </div>
        <div className="text-center py-4 text-sm text-foreground-tertiary">{error}</div>
      </div>
    );
  }

  const criticalCount = stats?.critical_entities?.length || 0;
  const spofCount = stats?.spof_count || 0;

  return (
    <div className="bg-surface/50 rounded-xl border border-border-muted overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border-muted">
        <h3 className="text-sm font-medium text-foreground">Reliability Overview</h3>
        <Link
          href="/command-center/dependencies"
          className="text-xs text-accent hover:text-accent transition-colors"
        >
          View Details →
        </Link>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-2 gap-px bg-cream/10">
        {/* SPOF Count */}
        <div className="p-4 bg-surface/50">
          <div className="flex items-center gap-2 mb-1">
            <span className={`w-2 h-2 rounded-full ${spofCount > 0 ? 'bg-red-500' : 'bg-green-500'}`} />
            <span className="text-xs text-foreground-tertiary">Single Points of Failure</span>
          </div>
          <div className={`text-2xl font-bold ${spofCount > 0 ? 'text-red-400' : 'text-green-400'}`}>
            {spofCount}
          </div>
        </div>

        {/* Critical entities */}
        <div className="p-4 bg-surface/50">
          <div className="flex items-center gap-2 mb-1">
            <span className={`w-2 h-2 rounded-full ${criticalCount > 0 ? 'bg-yellow-500' : 'bg-green-500'}`} />
            <span className="text-xs text-foreground-tertiary">At-Risk Entities</span>
          </div>
          <div className={`text-2xl font-bold ${criticalCount > 0 ? 'text-yellow-400' : 'text-green-400'}`}>
            {criticalCount}
          </div>
        </div>
      </div>

      {/* Critical entities list */}
      {criticalCount > 0 && (
        <div className="p-4 border-t border-border-muted">
          <div className="text-xs text-foreground-tertiary mb-2">Critical Entities</div>
          <div className="space-y-2">
            {stats?.critical_entities?.slice(0, 5).map((entity) => (
              <Link
                key={entity.id}
                href={`/command-center/entities3?search=${encodeURIComponent(entity.name)}`}
                className="flex items-center justify-between p-2 bg-background/50 rounded-lg hover:bg-background/70 transition-colors group"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <span
                    className={`w-2 h-2 rounded-full flex-shrink-0 ${
                      entity.is_single_point_of_failure
                        ? 'bg-red-500'
                        : entity.risk_reason.includes('Availability')
                        ? 'bg-yellow-500'
                        : 'bg-accent-500'
                    }`}
                  />
                  <span className="text-sm text-foreground truncate group-hover:text-accent transition-colors">
                    {entity.name}
                  </span>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className="text-xs text-foreground-muted">{entity.entity_type}</span>
                  {entity.is_single_point_of_failure && (
                    <span className="px-1.5 py-0.5 text-xs bg-red-900/50 text-red-400 rounded">
                      SPOF
                    </span>
                  )}
                </div>
              </Link>
            ))}
          </div>
          {criticalCount > 5 && (
            <Link
              href="/command-center/dependencies"
              className="block mt-2 text-xs text-center text-foreground-muted hover:text-foreground transition-colors"
            >
              +{criticalCount - 5} more
            </Link>
          )}
        </div>
      )}

      {/* Empty state */}
      {!stats?.critical_entities?.length && spofCount === 0 && (
        <div className="p-6 text-center">
          <div className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-green-900/30 mb-2">
            <svg className="w-5 h-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <p className="text-sm text-foreground-tertiary">All systems healthy</p>
          <p className="text-xs text-foreground-muted mt-1">No critical entities detected</p>
        </div>
      )}
    </div>
  );
}

export default ReliabilityWidget;
